import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SUPPORTED_LOCALES,
  type Locale,
  type LocalizedEmailTemplates,
} from "@offertli/shared";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Textarea } from "@primereact/ui/textarea";
import { Section } from "../shared.js";
const LANGUAGE_NAMES: Record<Locale, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
};
export function EmailTemplateEditor({
  offer,
  invoice,
  onOfferChange,
  onInvoiceChange,
}: {
  offer: LocalizedEmailTemplates;
  invoice: LocalizedEmailTemplates;
  onOfferChange: (value: LocalizedEmailTemplates) => void;
  onInvoiceChange: (value: LocalizedEmailTemplates) => void;
}) {
  const { t } = useTranslation();
  const [locale, setLocale] = useState<Locale>("en");
  const update = (
    kind: "offer" | "invoice",
    field: "subject" | "body",
    value: string,
  ) => {
    const current = kind === "offer" ? offer : invoice;
    const next = {
      ...current,
      [locale]: {
        ...current[locale],
        [field]: value,
      },
    };
    if (kind === "offer") onOfferChange(next);
    else onInvoiceChange(next);
  };
  return (
    <Section
      title={t("settings.emailTemplates.title", {
        defaultValue: "Offer and invoice emails",
      })}
    >
      <p className="mb-3 text-sm text-app-muted">
        {t("settings.emailTemplates.hint", {
          defaultValue:
            "Customize the email sent with each PDF. Available placeholders: {{companyName}}, {{customerName}}, {{number}}, {{title}}, {{total}}, {{dueDate}} and {{shareUrl}}.",
          companyName: "{{companyName}}",
          customerName: "{{customerName}}",
          number: "{{number}}",
          title: "{{title}}",
          total: "{{total}}",
          dueDate: "{{dueDate}}",
          shareUrl: "{{shareUrl}}",
        })}
      </p>
      <div
        className="mb-4 flex flex-wrap gap-1 rounded-lg bg-[var(--p-content-hover-background)] p-1"
        role="tablist"
        aria-label={t("settings.emailTemplates.language", {
          defaultValue: "Template language",
        })}
      >
        {SUPPORTED_LOCALES.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={locale === value}
            onClick={() => setLocale(value)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${locale === value ? "bg-app-surface text-app-text shadow-sm" : "text-app-muted"}`}
          >
            {LANGUAGE_NAMES[value]}
          </button>
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        {(["offer", "invoice"] as const).map((kind) => {
          const templates = kind === "offer" ? offer : invoice;
          return (
            <fieldset key={kind} className="space-y-3 rounded-lg border p-4">
              <legend className="px-1 font-semibold text-app-text">
                {kind === "offer"
                  ? t("settings.emailTemplates.offer", {
                      defaultValue: "Offer email",
                    })
                  : t("settings.emailTemplates.invoice", {
                      defaultValue: "Invoice email",
                    })}
              </legend>
              <Field
                label={t("settings.emailTemplates.subject", {
                  defaultValue: "Subject",
                })}
                required
              >
                <Input
                  required
                  maxLength={300}
                  value={templates[locale].subject}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    update(kind, "subject", event.target.value)
                  }
                />
              </Field>
              <Field
                label={t("settings.emailTemplates.body", {
                  defaultValue: "Message",
                })}
                required
              >
                <Textarea
                  required
                  rows={9}
                  maxLength={10000}
                  value={templates[locale].body}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                    update(kind, "body", event.target.value)
                  }
                />
              </Field>
            </fieldset>
          );
        })}
      </div>
    </Section>
  );
}
